import { useState, FC, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Header,
  SpaceBetween,
  Button,
  Table,
  ContentLayout,
  Box,
  Link,
  FormField,
  Select,
  ColumnLayout,
  Flashbar,
  ExpandableSection,
  Modal,
  Alert
} from '@amzn/awsui-components-react';
import { ToolVersionRevisionNumber, VersionRevision, ToolMetadata } from 'src/store/interfaces/Tool.types';
import { 
  useGetToolVersionRevisionsQuery, 
  useGetToolMetadataByIdQuery,
  useUpdateToolRevisionAssignmentsMutation,
  useDeleteToolVersionMutation,
  useDeleteToolMutation
} from 'src/store/apis/toolApi';
import useNotification from '../../hooks/useNotification';
import { usePermissions } from '../../hooks/usePermissions';
import { TOOL_STATUS, ToolStatus } from '../../constants';
import StatusBadge from '../common/StatusBadge';

// Define phase configuration
type PhaseKey = 'testRevision' | 'betaRevision' | 'liveRevision';

interface PhaseConfig {
  key: PhaseKey;
  label: string;
  displayName: string;
}

// Consolidated state interface for better state management
interface PhaseState {
  revision?: VersionRevision;
  version: string | null;
  revisionNumber: number | null;
}

type ConsolidatedPhaseState = Record<PhaseKey, PhaseState>;

const PHASES: PhaseConfig[] = [
  { key: 'testRevision', label: 'Test Phase', displayName: 'Test' },
  { key: 'betaRevision', label: 'Beta Phase', displayName: 'Beta' },
  { key: 'liveRevision', label: 'Live Phase', displayName: 'Live' }
];

const EditTool: FC = () => {
  const { toolId } = useParams<{ toolId: string }>();
  const navigate = useNavigate();
  const { addNotificationItem, notificationItems } = useNotification();
  const { canEditTools } = usePermissions();
  
  if (!toolId) {
    return (
      <ContentLayout>
        <Box textAlign="center" color="inherit">
          <b>Tool ID not found</b>
          <Box padding={{ bottom: "s" }} variant="p" color="inherit">
            Please provide a valid tool ID.
          </Box>
        </Box>
      </ContentLayout>
    );
  }
  
  const { data: versionRevisions, error: versionsError, isLoading: versionsLoading } = useGetToolVersionRevisionsQuery({ toolId });
  const { data: toolMetadata, error: metadataError, isLoading: metadataLoading } = useGetToolMetadataByIdQuery({ toolId });
  
  // Consolidated state management for phases
  const [phaseState, setPhaseState] = useState<ConsolidatedPhaseState>({
    testRevision: { version: null, revisionNumber: null },
    betaRevision: { version: null, revisionNumber: null },
    liveRevision: { version: null, revisionNumber: null }
  });

  // Confirmation dialog state
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  // Delete version state
  const [deleteToolVersion, { isLoading: isDeleting }] = useDeleteToolVersionMutation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [versionToDelete, setVersionToDelete] = useState<string | null>(null);

  // Delete entire tool state
  const [deleteTool, { isLoading: isDeletingTool }] = useDeleteToolMutation();
  const [showDeleteToolModal, setShowDeleteToolModal] = useState(false);
  
  useEffect(() => {
    if (toolMetadata) {
      // Update consolidated state for all phases
      const newPhaseState: ConsolidatedPhaseState = {
        testRevision: {
          revision: toolMetadata.testRevision,
          version: toolMetadata.testRevision?.version ?? null,
          revisionNumber: toolMetadata.testRevision?.revision ?? null
        },
        betaRevision: {
          revision: toolMetadata.betaRevision,
          version: toolMetadata.betaRevision?.version ?? null,
          revisionNumber: toolMetadata.betaRevision?.revision ?? null
        },
        liveRevision: {
          revision: toolMetadata.liveRevision,
          version: toolMetadata.liveRevision?.version ?? null,
          revisionNumber: toolMetadata.liveRevision?.revision ?? null
        }
      };
      
      setPhaseState(newPhaseState);
    }
  }, [toolMetadata]);
  
  const [updateRevisionAssignments, { isLoading: isUpdating }] = useUpdateToolRevisionAssignmentsMutation();

  const handleViewRevision = (version: string, revision: number) => {
    navigate(`/tools/${toolId}/version/${version}/revision/${revision}`);
  };

  const getVersionStatus = (version: string | null): ToolStatus | undefined => {
    if (!version) return undefined;
    
    if (versionRevisions) {
      const versionData = versionRevisions.find(v => v.version === version);
      if (versionData && versionData.status) {
        return versionData.status === TOOL_STATUS.VALID ? TOOL_STATUS.VALID : TOOL_STATUS.INVALID;
      }
    }
    
    const allAssignments = [
      toolMetadata?.testRevision,
      toolMetadata?.betaRevision,
      toolMetadata?.liveRevision
    ].filter(assignment => assignment?.version === version);
    
    const hasInvalidAssignment = allAssignments.some(assignment => 
      assignment?.status === TOOL_STATUS.INVALID
    );
    
    if (hasInvalidAssignment) {
      return TOOL_STATUS.INVALID;
    }
    
    return TOOL_STATUS.VALID;
  };

  const isVersionInvalid = (version: string | null): boolean => {
    if (!version) return false;
    return getVersionStatus(version) === TOOL_STATUS.INVALID;
  };

  // Validation function to check if version and revision are both selected or both unselected
  const isValidRevisionSelection = (version: string | null, revision: number | null): boolean => {
    return (version !== null && revision !== null) || (version === null && revision === null);
  };

  // Check if all selections are valid for saving
  const hasValidSelections = useMemo(() => {
    const phaseValidations = PHASES.map(phase => {
      const currentVersion = phaseState[phase.key].version;
      const currentRevision = phaseState[phase.key].revisionNumber;
      
      // Check if the current UI selection is consistent (both selected or both null)
      const isSelectionConsistent = isValidRevisionSelection(currentVersion, currentRevision);
      if (!isSelectionConsistent) {
        return false;
      }
      
      // If user has unassigned (set to None), that's always valid
      if (!currentVersion) {
        return true;
      }
      
      // If invalid version is selected, mark as invalid
      if (isVersionInvalid(currentVersion)) {
        return false;
      }
      
      return true;
    });
    return phaseValidations.every(valid => valid);
  }, [phaseState]);

  // Check if Live phase is being changed
  const hasLivePhaseChanges = useMemo(() => {
    if (!toolMetadata) return false;
    
    const currentLiveVersion = phaseState.liveRevision.version;
    const currentLiveRevision = phaseState.liveRevision.revisionNumber;
    const originalLiveVersion = toolMetadata.liveRevision?.version ?? null;
    const originalLiveRevision = toolMetadata.liveRevision?.revision ?? null;
    
    return currentLiveVersion !== originalLiveVersion || currentLiveRevision !== originalLiveRevision;
  }, [toolMetadata, phaseState]);


  // Handler for Save Changes button click - checks if confirmation is needed
  const handleSaveChangesClick = () => {
    if (hasLivePhaseChanges) {
      setShowConfirmationModal(true);
    } else {
      handleSaveRevisionAssignments();
    }
  };

  // Handler to confirm and proceed with save
  const handleConfirmSave = () => {
    setShowConfirmationModal(false);
    handleSaveRevisionAssignments();
  };

  // Handler to cancel the save operation
  const handleCancelSave = () => {
    setShowConfirmationModal(false);
  };

  // Delete version handlers
  const handleDeleteVersion = (version: string) => {
    setVersionToDelete(version);
    setShowDeleteModal(true);
  };

  const getErrorMessage = (error: unknown): string => {
    if (error && typeof error === 'object') {
      if ('data' in error && error.data) {
        const data = error.data as Record<string, unknown>;
        return String(data.message || data.error || JSON.stringify(data));
      }
      if ('error' in error && typeof (error as { error: unknown }).error === 'string') {
        return (error as { error: string }).error;
      }
      if ('message' in error && typeof (error as { message: unknown }).message === 'string') {
        return (error as { message: string }).message;
      }
    }
    return 'Unknown error';
  };

  const handleConfirmDelete = async () => {
    if (!toolId || !versionToDelete) return;
    try {
      await deleteToolVersion({ toolId, version: versionToDelete }).unwrap();
      addNotificationItem({
        type: 'success',
        content: `Version "${versionToDelete}" deleted successfully. You can now remove the config file from source.`,
        dismissible: true,
      });
    } catch (error) {
      addNotificationItem({
        type: 'error',
        content: `Failed to delete version "${versionToDelete}": ${getErrorMessage(error)}`,
        dismissible: true,
      });
    } finally {
      setShowDeleteModal(false);
      setVersionToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setVersionToDelete(null);
  };

  // Delete entire tool handlers
  const handleConfirmDeleteTool = async () => {
    if (!toolId) return;
    try {
      await deleteTool({ toolId }).unwrap();
      addNotificationItem({
        type: 'success',
        content: `Tool "${toolId}" deleted successfully. Remove its config file(s) from source and deploy to finalise the deletion.`,
        dismissible: true,
      });
      setShowDeleteToolModal(false);
      navigate('/tools');
    } catch (error) {
      addNotificationItem({
        type: 'error',
        content: `Failed to delete tool "${toolId}": ${getErrorMessage(error)}`,
        dismissible: true,
      });
      setShowDeleteToolModal(false);
    }
  };

  const handleCancelDeleteTool = () => {
    setShowDeleteToolModal(false);
  };

  const handleSaveRevisionAssignments = async () => {
    if (!toolMetadata) return;
    
    try {
      // Only include phases that have actually changed
      const changedPhases: Partial<Record<PhaseKey, VersionRevision>> = {};
      const changedPhaseNames: string[] = [];
      
      PHASES.forEach(phase => {
        const currentVersion = phaseState[phase.key].version;
        const currentRevision = phaseState[phase.key].revisionNumber;
        const originalVersion = toolMetadata[phase.key]?.version ?? null;
        const originalRevision = toolMetadata[phase.key]?.revision ?? null;
        
        // Check if this phase has changed
        const hasChanged = currentVersion !== originalVersion || currentRevision !== originalRevision;
        
        if (hasChanged) {
          changedPhases[phase.key] = currentVersion && currentRevision !== null ? 
            { version: currentVersion, revision: currentRevision } : 
            { version: null, revision: null };
          changedPhaseNames.push(phase.displayName);
        }
      });
      
      // Build payload with only changed phases
      const payload: ToolMetadata = {
        toolId: toolId,
        ...changedPhases
      };

      await updateRevisionAssignments({ toolId, data: payload }).unwrap();
      const phaseText = changedPhaseNames.length > 0 
        ? ` for ${changedPhaseNames.join(', ')} phase(s)`
        : '';
      
      addNotificationItem({
        type: 'success',
        content: `Successfully updated revision assignments${phaseText} for ${toolId}`,
        id: 'revision-assignments-updated',
      });

    } catch (error) {
      console.error('Update error:', error);
      const errorMessage = (error as Error).message;

      addNotificationItem({
        type: 'error',
        content: `Error updating revision assignments: ${errorMessage}`,
        id: 'revision-assignments-error'
      });
    }
  };


  const versionOptions = useMemo(() => {
    const options = [{ label: 'None', value: '' }];
    
    if (versionRevisions) {
      versionRevisions.forEach(versionData => {
        const status = getVersionStatus(versionData.version);
        const statusLabel = status === TOOL_STATUS.INVALID ? ` (${TOOL_STATUS.INVALID})` : ` (${TOOL_STATUS.VALID})`;
        
        options.push({
          label: `${versionData.version}${statusLabel}`,
          value: versionData.version
        });
      });
    }
    
    return options;
  }, [versionRevisions, toolMetadata]);

  // Create revision options for a specific version
  const getRevisionOptions = (selectedVersion: string | null) => {
    const options = [{ label: 'None', value: '' }];
    
    if (selectedVersion && versionRevisions) {
      const versionData = versionRevisions.find(v => v.version === selectedVersion);
      if (versionData) {
        versionData.revisions.forEach(revision => {
          options.push({
            label: `${revision.revision}`,
            value: revision.revision.toString()
          });
        });
      }
    }
    
    return options;
  };

  const hasChanges = useMemo(() => {
    if (!toolMetadata) return false;
    
    return PHASES.some(phase => 
      phaseState[phase.key].version !== toolMetadata[phase.key]?.version ||
      phaseState[phase.key].revisionNumber !== toolMetadata[phase.key]?.revision
    );
  }, [toolMetadata, phaseState]);

  // Helper function to render status badge for phase validation
  const renderPhaseStatusBadge = (phaseKey: PhaseKey) => {
    const currentVersion = phaseState[phaseKey].version;
    const currentRevision = phaseState[phaseKey].revisionNumber;
    
    if (!currentVersion) {
      return <StatusBadge status="NO_ASSIGNMENT" />;
    }
    
    // Show invalid badge immediately when invalid version is selected
    if (isVersionInvalid(currentVersion)) {
      return <StatusBadge status={TOOL_STATUS.INVALID} />;
    }
    
    // Show valid badge when both version and revision are selected
    if (currentRevision !== null) {
      return <StatusBadge status={TOOL_STATUS.VALID} />;
    }
    
    // Show pending when version is selected but no revision yet
    return <StatusBadge status="PENDING_REVISION" />;
  };

  // Version table column definitions
  const versionColumnDefinitions = [
    {
      id: 'version',
      header: 'Version',
      cell: (item: ToolVersionRevisionNumber) => {
        const status = getVersionStatus(item.version);
        const isInvalid = status === TOOL_STATUS.INVALID;
        const baseDescription = `Created: ${new Date(item.createdTime).toLocaleString()}`;
        const headerDescription = isInvalid && item.status_message 
          ? `${baseDescription} | Status: ${item.status_message}`
          : baseDescription;
        
        return (
          <ExpandableSection
            headerText={
              <SpaceBetween direction="horizontal" size="s" alignItems="center">
                <span>{item.version}</span>
                <StatusBadge status={status || TOOL_STATUS.VALID} />
              </SpaceBetween>
            }
            headerDescription={headerDescription}
          >
          <Table
            columnDefinitions={[
              {
                id: 'revision',
                header: 'Revision',
                cell: (rev: { revision: number; createdTime: string; createdBy: string }) => (
                  <Link onFollow={() => handleViewRevision(item.version, rev.revision)}>
                    {rev.revision}
                  </Link>
                )
              },
              {
                id: 'createdTime',
                header: 'Created',
                cell: (rev: { revision: number; createdTime: string; createdBy: string }) => new Date(rev.createdTime).toLocaleString()
              },
              {
                id: 'createdBy',
                header: 'Created By',
                cell: (rev: { revision: number; createdTime: string; createdBy: string }) => rev.createdBy
              }
            ]}
            items={item.revisions}
            variant="embedded"
          />
          {canEditTools && (
            <Box padding={{ top: "s" }}>
              <Button
                variant="link"
                onClick={() => handleDeleteVersion(item.version)}
              >
                Delete version
              </Button>
            </Box>
          )}
        </ExpandableSection>
        );
      }
    }
  ];

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={`Manage revisions for ${toolId}`}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => navigate('/tools')}>
                Back to Tools
              </Button>
              {canEditTools && (
                <Button
                  onClick={() => setShowDeleteToolModal(true)}
                  loading={isDeletingTool}
                >
                  Delete tool
                </Button>
              )}
            </SpaceBetween>
          }
        >
          {toolId}
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Flashbar items={notificationItems} stackItems />
        
        {!canEditTools && (
          <Alert type="info" statusIconAriaLabel="Info">
            You have view-only access to this page. Sufficient permission is required to make changes.
          </Alert>
        )}
        
        {/* Revision Management Section */}
        <Container
          header={
            <Header
              variant="h2"
              description="Assign revisions to different phases"
              actions={
                <Button
                  variant="primary"
                  disabled={!hasChanges || isUpdating || !hasValidSelections || !canEditTools}
                  loading={isUpdating}
                  onClick={handleSaveChangesClick}
                >
                  Save Changes
                </Button>
              }
            >
              Revision Assignments
            </Header>
          }
        >
          <ColumnLayout columns={3} variant="text-grid">
            {PHASES.map((phase) => (
              <SpaceBetween key={phase.key} size="s">
                <SpaceBetween direction="horizontal" size="s" alignItems="center">
                  <Header variant="h3">{phase.displayName} Phase</Header>
                  {renderPhaseStatusBadge(phase.key)}
                </SpaceBetween>
                <FormField
                  label="Version"
                  errorText={
                    phaseState[phase.key].version && isVersionInvalid(phaseState[phase.key].version)
                      ? "Invalid version selected - this version has validation issues"
                      : undefined
                  }
                >
                  <Select
                    selectedOption={
                      phaseState[phase.key].version 
                        ? { label: phaseState[phase.key].version!, value: phaseState[phase.key].version! }
                        : { label: 'None', value: '' }
                    }
                    onChange={({ detail }) => {
                      const version = detail.selectedOption.value && detail.selectedOption.value !== '' ? detail.selectedOption.value : null;
                      setPhaseState(prev => ({ 
                        ...prev, 
                        [phase.key]: { 
                          ...prev[phase.key], 
                          version, 
                          revisionNumber: null 
                        } 
                      }));
                    }}
                    options={versionOptions}
                    placeholder="Select version"
                    disabled={!canEditTools}
                  />
                </FormField>
                <FormField
                  label="Revision"
                >
                  <Select
                    selectedOption={
                      phaseState[phase.key].revisionNumber !== null
                        ? { label: `${phaseState[phase.key].revisionNumber}`, value: phaseState[phase.key].revisionNumber!.toString() }
                        : { label: 'None', value: '' }
                    }
                    onChange={({ detail }) => {
                      const revision = detail.selectedOption.value && detail.selectedOption.value !== '' ? parseInt(detail.selectedOption.value) : null;
                      setPhaseState(prev => ({ 
                        ...prev, 
                        [phase.key]: { 
                          ...prev[phase.key], 
                          revisionNumber: revision 
                        } 
                      }));
                    }}
                    options={getRevisionOptions(phaseState[phase.key].version)}
                    placeholder="Select revision"
                    disabled={!phaseState[phase.key].version || !canEditTools}
                  />
                </FormField>
              </SpaceBetween>
            ))}
          </ColumnLayout>
        </Container>

        {/* Versions and Revisions Table */}
        <Container>
          <Table
            columnDefinitions={versionColumnDefinitions}
            items={versionRevisions || []}
            sortingDisabled={false}
            header={
              <Header
                counter={`(${versionRevisions?.length || 0})`}
              >
                Versions and Revisions
              </Header>
            }
            empty={
              <Box textAlign="center" color="inherit">
                <b>No versions</b>
                <Box padding={{ bottom: "s" }} variant="p" color="inherit">
                  No versions found for this tool.
                </Box>
              </Box>
            }
          />
        </Container>

        {/* Live Phase Update Confirmation Modal */}
        <Modal
          visible={showConfirmationModal}
          onDismiss={handleCancelSave}
          header="Confirm Live Phase Update"
          closeAriaLabel="Close modal"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={handleCancelSave}>
                  Cancel
                </Button>
                <Button 
                  variant="primary" 
                  onClick={handleConfirmSave}
                  loading={isUpdating}
                >
                  Confirm Update
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween direction="vertical" size="m">
            <Box variant="span">
              You are about to update the <strong>Live Phase</strong> revision assignment. 
              This change will affect the production environment and impact live users.
            </Box>
            <Box variant="span">
              Are you sure you want to proceed with this update?
            </Box>
            {phaseState.liveRevision.version && phaseState.liveRevision.revisionNumber !== null && (
              <Box variant="span" color="text-status-info">
                <SpaceBetween direction="vertical" size="xs">
                  <Box variant="span">
                    <strong>New Live Assignment:</strong>
                  </Box>
                  <Box variant="span" padding={{ left: "s" }}>
                    <strong>Version:</strong> {phaseState.liveRevision.version}
                  </Box>
                  <Box variant="span" padding={{ left: "s" }}>
                    <strong>Revision:</strong> {phaseState.liveRevision.revisionNumber}
                  </Box>
                </SpaceBetween>
              </Box>
            )}
            {!phaseState.liveRevision.version && (
              <Box variant="span" color="text-status-warning">
                <strong>Warning:</strong> This will remove the current Live Phase assignment, leaving no active revision in production.
              </Box>
            )}
          </SpaceBetween>
        </Modal>

        {/* Delete Version Confirmation Modal */}
        <Modal
          visible={showDeleteModal}
          onDismiss={handleCancelDelete}
          header="Delete Tool Version"
          closeAriaLabel="Close modal"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={handleCancelDelete}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleConfirmDelete}
                  loading={isDeleting}
                >
                  Delete
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween direction="vertical" size="m">
            <Alert type="warning" statusIconAriaLabel="Warning">
              This action cannot be undone.
            </Alert>
            <Box variant="span">
              You are about to delete version <strong>{versionToDelete}</strong> and all its
              associated phase assignments and revisions from the database.
            </Box>
            <Box variant="span">
              After deletion, you must also remove the corresponding config file from source
              and deploy to complete the deprecation process.
            </Box>
          </SpaceBetween>
        </Modal>

        {/* Delete Entire Tool Confirmation Modal */}
        <Modal
          visible={showDeleteToolModal}
          onDismiss={handleCancelDeleteTool}
          header="Delete Tool"
          closeAriaLabel="Close modal"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={handleCancelDeleteTool}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleConfirmDeleteTool}
                  loading={isDeletingTool}
                >
                  Delete tool
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween direction="vertical" size="m">
            <Alert type="warning" statusIconAriaLabel="Warning">
              This action cannot be undone.
            </Alert>
            <Box variant="span">
              You are about to delete the entire tool <strong>{toolId}</strong>, including
              <strong> all of its versions</strong>, revisions, and phase assignments. The tool
              will stop being served immediately.
            </Box>
            <Box variant="span">
              After deletion, remove the tool's config file(s) from source and deploy to finalise
              the removal. Any test suites or UAT sessions that reference this tool may be affected.
            </Box>
          </SpaceBetween>
        </Modal>
      </SpaceBetween>
    </ContentLayout>
  );
};

export default EditTool;
